// src/components/booking/BookingForm.jsx
import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import {
	doc,
	runTransaction,
	serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { BOOKING_CONTACT_EMAIL } from "../../data/bookingServices";
import { API_BASE, createZoomMeeting, makeCalendarInviteUrl } from "../../data/bookingLinks";
import { isAppointmentStartTimeAllowed, isTimeBlocked } from "./bookingAvailability";
import "../booking/bookings.css";

// ── EmailJS config ────────────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID = 'service_jpezr0s';
const EMAILJS_TEMPLATE_ID = 'template_h6tfu65';
const EMAILJS_PUBLIC_KEY = 'Qc73pj33bbA0EKVxQ';

// ── Referral source options ───────────────────────────────────────────────────
const REFERRAL_SOURCES = [
	{ id: "linkedin", label: "LinkedIn" },
	{ id: "facebook", label: "Facebook" },
	{ id: "tiktok", label: "TikTok" },
	{ id: "youtube", label: "YouTube" },
	{ id: "google", label: "Google" },
	{ id: "instagram", label: "Instagram" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function bookingDocId(date, time) {
	return `${date}_${time}`;
}

function toYearMonth(dateStr) {
	return dateStr.slice(0, 7);
}

function referralLabel(id) {
	const match = REFERRAL_SOURCES.find((s) => s.id === id);
	return match ? match.label : "Not provided";
}

function createRescheduleToken() {
	return Array.from(
		crypto.getRandomValues(new Uint8Array(32)),
		(byte) => byte.toString(16).padStart(2, "0")
	).join("");
}

async function hashRescheduleToken(token) {
	const bytes = new TextEncoder().encode(token);
	const digest = await crypto.subtle.digest("SHA-256", bytes);
	return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function rescheduleUrl(token) {
	const url = new URL("/booking/reschedule", window.location.origin);
	url.searchParams.set("token", token);
	return url.toString();
}

function isPayableOnline(service) {
	return service.allowOnlinePayment === true
		&& !service.quoteOnly
		&& Number.isFinite(Number(service.priceCents))
		&& Number(service.priceCents) > 0;
}

// ── Component ─────────────────────────────────────────────────────────────────

const BookingForm = ({ service, date, time, onBack, onSubmit }) => {
	const [formData, setFormData] = useState({
		name: "",
		surname: "",
		email: "",
		cellphone: "",
		referralSource: null,
	});
	const [errors, setErrors] = useState({});
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState(null);
	const [payLaterReminderOpen, setPayLaterReminderOpen] = useState(false);
	const payLaterAcknowledgedRef = useRef(false);

	const payableOnline = isPayableOnline(service);

	// No default selection when payment is required — the person must
	// actively choose. Services with no online payment option (e.g. a
	// custom quote) skip this gate entirely, since there's nothing to pick.
	const [paymentChoice, setPaymentChoice] = useState(payableOnline ? null : "later");
	const [paymentTouched, setPaymentTouched] = useState(!payableOnline);

	// Everything below the payment step is locked until a method is chosen.
	const formLocked = payableOnline && !paymentChoice;

	const handleChange = (field) => (e) => {
		setFormData((prev) => ({ ...prev, [field]: e.target.value }));
		if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
	};

	const handleReferralSelect = (id) => {
		if (formLocked) return;
		setFormData((prev) => ({
			...prev,
			referralSource: prev.referralSource === id ? null : id,
		}));
	};

	const selectPayment = (choice) => {
		setPaymentChoice(choice);
		setPaymentTouched(true);
	};

	const validate = () => {
		const next = {};
		if (!formData.name.trim()) next.name = "Name is required";
		if (!formData.surname.trim()) next.surname = "Surname is required";
		if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
			next.email = "Enter a valid email address";
		}
		if (!/^[0-9+\s]{7,15}$/.test(formData.cellphone)) {
			next.cellphone = "Enter a valid cellphone number";
		}
		setErrors(next);
		return Object.keys(next).length === 0;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!isAppointmentStartTimeAllowed(time, service.mode)) {
			setSubmitError(
				service.mode === "in-person"
					? "The last available start time for in-person appointments is 17:00. Please choose another time."
					: "The last available start time for online appointments is 18:00. Please choose another time."
			);
			return;
		}
		if (payableOnline && !paymentChoice) {
			setPaymentTouched(true);
			return;
		}
		if (!validate()) return;
		if (payableOnline && paymentChoice === "later" && !payLaterAcknowledgedRef.current) {
			setPayLaterReminderOpen(true);
			return;
		}

		setSubmitting(true);
		setSubmitError(null);

		const docId = bookingDocId(date, time);
		const bookingRef = doc(db, "bookings", docId);

		const formattedDate = new Date(`${date}T00:00:00`).toLocaleDateString("en-ZA", {
			weekday: "long", day: "numeric", month: "long", year: "numeric",
		});

		const wantsPayNow = payableOnline && paymentChoice === "now";

		try {
			const rescheduleToken = createRescheduleToken();
			const rescheduleTokenHash = await hashRescheduleToken(rescheduleToken);
			await runTransaction(db, async (transaction) => {
				const existing = await transaction.get(bookingRef);
				if (existing.exists()) {
					throw new Error("SLOT_TAKEN");
				}
				const availability = await transaction.get(doc(db, "settings", "bookingAvailability"));
				const availabilityData = availability.exists() ? availability.data() : {};
				const blockedDates = Array.isArray(availabilityData.blockedDates)
					? availabilityData.blockedDates
					: [];
				if (blockedDates.includes(date)) throw new Error("DAY_BLOCKED");
				const blockedSlots = Array.isArray(availabilityData.blockedSlots)
					? availabilityData.blockedSlots
					: [];
				const blockedTimeRanges = Array.isArray(availabilityData.blockedTimeRanges)
					? availabilityData.blockedTimeRanges
					: [];
				if (isTimeBlocked(date, time, blockedSlots, blockedTimeRanges)) throw new Error("SLOT_BLOCKED");
				transaction.set(bookingRef, {
					serviceId: service.id,
					serviceTitle: service.bookingTitle || service.title,
					servicePackageId: service.packageId || null,
					servicePackageLabel: service.packageLabel || null,
					mode: service.mode,
					price: service.price || "",
					priceCents: Number.isFinite(Number(service.priceCents)) ? Number(service.priceCents) : null,
					quoteOnly: service.quoteOnly === true,
					paymentChoice: payableOnline ? paymentChoice : service.quoteOnly ? "quote" : "later",
					onlinePaymentEnabled: payableOnline,
					duration: service.duration,
					date,
					time,
					yearMonth: toYearMonth(date),
					status: "confirmed",
					paymentStatus: "unpaid",
					name: formData.name.trim(),
					surname: formData.surname.trim(),
					email: formData.email.trim().toLowerCase(),
					cellphone: formData.cellphone.trim(),
					referralSource: formData.referralSource || null,
					rescheduleTokenHash,
					bookedAt: serverTimestamp(),
				});
			});

			if (wantsPayNow) {
				sessionStorage.setItem(`booking-reschedule:${docId}`, JSON.stringify({
					token: rescheduleToken,
					name: `${formData.name.trim()} ${formData.surname.trim()}`.trim(),
					email: formData.email.trim().toLowerCase(),
				}));
				const res = await fetch(`${API_BASE}/create-booking-checkout`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						bookingId: docId,
						buyerName: `${formData.name.trim()} ${formData.surname.trim()}`.trim(),
						buyerEmail: formData.email.trim().toLowerCase(),
					}),
				});
				const checkout = await res.json();
				if (!res.ok) {
					throw new Error(checkout.message || "Could not start payment. Please try again.");
				}
				window.location.href = checkout.redirectUrl;
				return;
			}

			let meetingUrl = "";
			let meetingSetupError = false;
			if (service.mode === "virtual") {
				try {
					meetingUrl = await createZoomMeeting(docId, rescheduleToken);
				} catch (zoomError) {
					console.error("Zoom meeting setup failed:", zoomError);
					meetingSetupError = true;
				}
			}

			// Pay-at-appointment — no webhook step exists for this path, so notify here.
			try {
				const notifyResponse = await fetch(`${API_BASE}/notify-booking`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ bookingId: docId }),
				});
				if (!notifyResponse.ok) throw new Error(`Booking notification failed (HTTP ${notifyResponse.status}).`);
			} catch (notifyError) {
				console.error("Admin booking notification failed:", notifyError);
			}

			const inviteUrl = makeCalendarInviteUrl(docId, rescheduleToken);
			const confirmationLinks = [
				`Add this appointment to your calendar: ${inviteUrl}`,
				meetingUrl ? `Join your virtual appointment on Zoom: ${meetingUrl}` : "",
			].filter(Boolean).join("\n");
			await emailjs.send(
				EMAILJS_SERVICE_ID,
				EMAILJS_TEMPLATE_ID,
				{
					to_name: formData.name.trim(),
					to_email: formData.email.trim().toLowerCase(),
					email_subject: "Booking confirmed",
					email_title: "Booking Confirmed",
					email_intro: "Your booking is confirmed. Here's a summary of your appointment:",
					report_body: confirmationLinks,
					action_label: "Reschedule appointment",
					action_url: rescheduleUrl(rescheduleToken),
					service_name: service.title,
					mode: service.mode === "in-person" ? "In Person" : "Virtual",
					booking_date: formattedDate,
					booking_time: time,
					duration: `${service.duration} min`,
					calendar_url: inviteUrl,
					meeting_url: meetingUrl,
					referral_source: referralLabel(formData.referralSource),
					reschedule_url: rescheduleUrl(rescheduleToken),
				},
				EMAILJS_PUBLIC_KEY
			);

			onSubmit(formData, {
				calendarInviteUrl: inviteUrl,
				meetingUrl,
				meetingSetupError,
			});

		} catch (err) {
			if (err.message === "SLOT_TAKEN") {
				setSubmitError(
					"Sorry, that slot was just taken. Please go back and choose another time."
				);
			} else if (err.message === "DAY_BLOCKED") {
				setSubmitError(
					"Sorry, this day is no longer available. Please go back and choose another date."
				);
			} else if (err.message === "SLOT_BLOCKED") {
				setSubmitError(
					"Sorry, this time is no longer available. Please go back and choose another time."
				);
			} else {
				console.error("Booking failed:", err);
				setSubmitError(
					err.message && wantsPayNow
						? err.message
						: "Something went wrong. Please try again or contact us directly."
				);
			}
		} finally {
			setSubmitting(false);
		}
	};

	const formattedDate = new Date(`${date}T00:00:00`).toLocaleDateString("en-ZA", {
		weekday: "long", day: "numeric", month: "long", year: "numeric",
	});

	return (
		<div className="booking-form-step">
			<div className="booking-step-heading">
				<div className="step-eyebrow">Step 3 of 3</div>
				<h2 className="step-title">Your details</h2>
				<p className="step-subtitle">
					{service.quoteOnly
						? "Share your details and we'll get back to you with a tailored quote."
						: "Just a few details and you're booked in."}
				</p>
			</div>

			<div className="booking-summary-card">
				<div className="booking-summary-row">
					<span className="booking-summary-label">Service</span>
					<span className="booking-summary-value">{service.title}</span>
				</div>
				{service.packageLabel && (
					<div className="booking-summary-row">
						<span className="booking-summary-label">Option</span>
						<span className="booking-summary-value">{service.packageLabel}</span>
					</div>
				)}
				<div className="booking-summary-row">
					<span className="booking-summary-label">When</span>
					<span className="booking-summary-value">
						{formattedDate} at {time}
					</span>
				</div>
				<div className="booking-summary-row">
					<span className="booking-summary-label">Duration</span>
					<span className="booking-summary-value">{service.duration} min</span>
				</div>
				<div className="booking-summary-row">
					<span className="booking-summary-label">Format</span>
					<span className="booking-summary-value">
						{service.mode === "in-person" ? "🏢 In Person" : "💻 Virtual"}
					</span>
				</div>
				{service.price && (
					<div className="booking-summary-row">
						<span className="booking-summary-label">Price</span>
						<span className="booking-summary-value">{service.price}</span>
					</div>
				)}
				<button type="button" className="booking-summary-change" onClick={onBack}>
					Change
				</button>
			</div>

			{/* ── Payment method — required before anything else unlocks ──────── */}
			{payableOnline && (
				<div className="payment-gate">
					<div className="payment-gate-heading">
						<label className="payment-gate-label">
							How would you like to pay?
							<span className="payment-gate-required">Required</span>
						</label>
					</div>

					<div className="payment-method-grid">
						<button
							type="button"
							className={[
								"payment-method-card",
								"payment-method-card--yoco",
								paymentChoice === "now" ? "is-selected" : "",
							].join(" ").trim()}
							onClick={() => selectPayment("now")}
							disabled={submitting}
							aria-pressed={paymentChoice === "now"}
						>
							<span className="payment-method-radio" aria-hidden="true" />
							<span className="payment-method-icon payment-method-icon--yoco">
								<svg viewBox="0 0 24 24" width="20" height="20" fill="none">
									<rect x="2" y="5" width="20" height="14" rx="3" fill="currentColor" opacity="0.15" />
									<rect x="2" y="5" width="20" height="14" rx="3" stroke="currentColor" strokeWidth="1.6" />
									<rect x="2" y="9" width="20" height="2.4" fill="currentColor" />
									<rect x="5" y="14" width="5" height="2" rx="1" fill="currentColor" />
								</svg>
							</span>
							<span className="payment-method-copy">
								<span className="payment-method-title">Pay now</span>
								<span className="payment-method-desc">Instant confirmation &amp; receipt</span>
							</span>
							<span className="payment-method-badge">
								<svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
									<path d="M12 1l3.5 2v5c0 5-3.5 8.5-3.5 8.5S8.5 13 8.5 8V3L12 1z" />
								</svg>
								Secured by Yoco
							</span>
						</button>

						<button
							type="button"
							className={[
								"payment-method-card",
								paymentChoice === "later" ? "is-selected" : "",
							].join(" ").trim()}
							onClick={() => selectPayment("later")}
							disabled={submitting}
							aria-pressed={paymentChoice === "later"}
						>
							<span className="payment-method-radio" aria-hidden="true" />
							<span className="payment-method-icon">
								<svg viewBox="0 0 24 24" width="20" height="20" fill="none">
									<circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
									<path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
								</svg>
							</span>
							<span className="payment-method-copy">
								<span className="payment-method-title">Pay at appointment</span>
								<span className="payment-method-desc">Settle when you arrive or join</span>
							</span>
						</button>
					</div>

					{!paymentChoice && paymentTouched && (
						<p className="payment-gate-error" role="alert">
							Choose how you'd like to pay to continue.
						</p>
					)}
				</div>
			)}

			{service.quoteOnly && (
				<div className="booking-query-callout">
					<strong>{service.title} is priced on request.</strong>
					<p>
						Complete the form to send a query, or email {" "}
						<a href={`mailto:${BOOKING_CONTACT_EMAIL}?subject=${encodeURIComponent(`${service.title} enquiry`)}`}>
							{BOOKING_CONTACT_EMAIL}
						</a>.
					</p>
				</div>
			)}

			<fieldset
				className={["booking-fieldset", formLocked ? "is-locked" : ""].join(" ").trim()}
				disabled={formLocked}
			>
				{formLocked && (
					<p className="booking-fieldset-hint">
						Select a payment method above to fill in your details.
					</p>
				)}

				<form className="booking-form" onSubmit={handleSubmit} noValidate>
					<div className="form-row">
						<div className="form-field">
							<label htmlFor="name">Name</label>
							<input
								id="name"
								type="text"
								autoComplete="given-name"
								value={formData.name}
								onChange={handleChange("name")}
								disabled={submitting || formLocked}
							/>
							{errors.name && <span className="form-error">{errors.name}</span>}
						</div>
						<div className="form-field">
							<label htmlFor="surname">Surname</label>
							<input
								id="surname"
								type="text"
								autoComplete="family-name"
								value={formData.surname}
								onChange={handleChange("surname")}
								disabled={submitting || formLocked}
							/>
							{errors.surname && <span className="form-error">{errors.surname}</span>}
						</div>
					</div>

					<div className="form-field">
						<label htmlFor="email">Email Address</label>
						<input
							id="email"
							type="email"
							autoComplete="email"
							value={formData.email}
							onChange={handleChange("email")}
							disabled={submitting || formLocked}
						/>
						{errors.email && <span className="form-error">{errors.email}</span>}
					</div>

					<div className="form-field">
						<label htmlFor="cellphone">Cellphone Number</label>
						<input
							id="cellphone"
							type="tel"
							autoComplete="tel"
							placeholder="e.g. 082 123 4567"
							value={formData.cellphone}
							onChange={handleChange("cellphone")}
							disabled={submitting || formLocked}
						/>
						{errors.cellphone && (
							<span className="form-error">{errors.cellphone}</span>
						)}
					</div>

					<div className="form-field">
						<label>
							How did you hear about us?{" "}
							<span className="form-optional-tag">(optional)</span>
						</label>
						<div className="referral-source-grid">
							{REFERRAL_SOURCES.map((src) => {
								const isSelected = formData.referralSource === src.id;
								return (
									<button
										key={src.id}
										type="button"
										className={[
											"referral-chip",
											isSelected ? "referral-chip-selected" : "",
										].join(" ").trim()}
										onClick={() => handleReferralSelect(src.id)}
										disabled={submitting || formLocked}
										aria-pressed={isSelected}
									>
										{src.label}
									</button>
								);
							})}
						</div>
					</div>

					{submitError && (
						<div className="form-submit-error" role="alert">
							{submitError}
						</div>
					)}

					<div className="step-actions">
						<button
							type="button"
							className="btn btn-ghost"
							onClick={onBack}
							disabled={submitting}
						>
							&larr; Back
						</button>
						<button
							type="submit"
							className="btn btn-primary"
							disabled={submitting || formLocked}
						>
							{submitting
								? (payableOnline && paymentChoice === "now" ? "Redirecting to payment…" : "Confirming…")
								: (payableOnline && paymentChoice === "now" ? "Continue to Payment" : service.quoteOnly ? "Send Query" : "Confirm Booking")}
						</button>
					</div>
					{payLaterReminderOpen && (
						<div className="booking-reminder-backdrop">
							<div
								className="booking-reminder-dialog"
								role="dialog"
								aria-modal="true"
								aria-labelledby="booking-reminder-title"
								aria-describedby="booking-reminder-copy"
							>
								<div className="booking-reminder-icon" aria-hidden="true">💳</div>
								<h3 id="booking-reminder-title">One little payment reminder</h3>
								<p id="booking-reminder-copy">
									You chose to pay at your appointment. Please remember to settle your session fee when you arrive or join. We’re looking forward to seeing you!
								</p>
								<div className="booking-reminder-actions">
									<button
										type="button"
										className="btn btn-ghost"
										onClick={() => setPayLaterReminderOpen(false)}
										disabled={submitting}
									>
										Go back
									</button>
									<button
										type="submit"
										className="btn btn-primary"
										onClick={() => {
											payLaterAcknowledgedRef.current = true;
											setPayLaterReminderOpen(false);
										}}
										disabled={submitting}
									>
										{submitting ? "Confirming…" : "Got it — confirm booking"}
									</button>
								</div>
							</div>
						</div>
					)}
				</form>
			</fieldset>
		</div>
	);
};

export default BookingForm;
